<template>
  <div class="user-statistic">
    <FlexLayout>
      <div
        v-for="statistic in balanceFields"
        :key="`balance-${statistic.label}`"
        class="user-info-item"
      >
        <p class="info-label">
          {{ statistic.label }}
          <el-tooltip v-if="statistic.tooltip" :content="statistic.tooltip" placement="top">
            <i class="el-icon-question" style="cursor: help" />
          </el-tooltip>
        </p>
        <p class="info-value">{{ statistic.value }}</p>
      </div>
    </FlexLayout>

    <div v-if="groupedPointFields.orgFields.length || groupedPointFields.shopFields.length" class="point-section">
      <div v-if="groupedPointFields.orgFields.length" class="point-group">
        <div class="point-group-items">
          <div
            v-for="statistic in groupedPointFields.orgFields"
            :key="`point-${statistic.fieldKey}`"
            class="user-info-item"
          >
            <p class="info-label flex items-center">
              <SVGIcon
                v-if="statistic.icon"
                :icon="statistic.icon"
                fill="transparent"
                stroke="var(--gray-80)"
                width="16"
                height="16"
                style="margin-right: 4px; vertical-align: middle"
              />
              {{ statistic.label }}
            </p>
            <p class="info-value">{{ statistic.value }}</p>
          </div>
        </div>
      </div>
      <div v-if="groupedPointFields.shopFields.length" class="point-group">
        <div class="point-group-items">
          <div
            v-for="statistic in groupedPointFields.shopFields"
            :key="`point-${statistic.fieldKey}`"
            class="user-info-item"
          >
            <p class="info-label flex items-center">
              <SVGIcon
                v-if="statistic.icon"
                :icon="statistic.icon"
                fill="transparent"
                stroke="var(--gray-80)"
                width="16"
                height="16"
                style="margin-right: 4px; vertical-align: middle"
              />
              {{ statistic.label }}
            </p>
            <p class="info-value">{{ statistic.value }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'
import { get, isNull } from 'lodash'
import { t } from '@/plugins/i18n/i18n'
import { usePermissions } from '@/use/permissions'
import { useMemberContext } from '@/use/useMemberContext'
import { useOrgBenefitControl } from '@/use/useOrgBenefitControl'

export default defineComponent({
  name: 'BalanceStatistic',
  props: {
    member: { type: Object, default: () => ({}) },
    reservationStatistic: { type: Object, default: () => ({}) },
    rebateWalletBalance: { type: Number, default: 0 },
    usePospal: { type: Boolean, default: false },
    useExternalPoint: { type: Boolean, default: false },
    pointWalletBalance: { type: Object, default: () => ({}) },
    pospalWalletBalance: { type: Number, default: 0 },
    externalPointWalletBalance: { type: [Number, String], default: '-' },
  },
  setup (props) {
    const { memberTopInfo, externalWalletName } = useMemberContext()
    const { checkAction } = usePermissions()
    const { isClassTicketOrgEnabled, enableOrgBenefitSharing } = useOrgBenefitControl()

    const useMemberWallet = computed(() => {
      return checkAction('admin.member.findOne.wallet')
    })

    const useShopRebate = computed(() => {
      return checkAction('adminView.member.shopCashback')
    })

    // 堂票、點數原本只看後端是否回傳資料；店家方案沒有該模組（GIMMATEK 不提供）時一併隱藏（CNCY-10766）
    const useClassTicket = computed(() => checkAction('admin.classTicketRecord.page'))
    const useShopPoint = computed(() => checkAction('planModule.shopPoint.enable'))

    const countSum = (count) => count.reduce((a, b) => a + b, 0) || 0

    const balanceFields = computed(() => {
      const fields = [
        {
          label: t('member.memberInfoBlock.reservationNoShow'),
          value: `${countSum(props.reservationStatistic.total)} / ${countSum(
            props.reservationStatistic.absence,
          )}`,
        },
      ]
      if (useMemberWallet.value) {
        fields.push({
          label: t('member.memberInfoBlock.depositBalance'),
          value: `$ ${get(props.member, 'Wallet.balance')}`,
        })
      }
      if (useShopRebate.value) {
        fields.push({
          label: t('member.memberInfoBlock.rebateBalance'),
          value: `$ ${props.rebateWalletBalance}`,
        })
      }
      if (props.usePospal) {
        const balance = props.pospalWalletBalance
        fields.push({
          label: externalWalletName.value,
          value: isNull(balance) ? '-' : `$ ${balance}`,
        })
      }

      // 堂票
      const classTicketSummary = memberTopInfo?.value?.classTicketSummary
      const classTicketCount = classTicketSummary?.totalOpenCount ?? memberTopInfo?.value?.classTicketRecordOpenCount ?? null
      if (useClassTicket.value && classTicketCount !== null && classTicketCount !== undefined) {
        const classTicketField = {
          label: t('member.memberInfoBlock.classTicket'),
          value: `${classTicketCount} ${t('member.memberInfoBlock.classTicketUnit')}`,
        }
        if (isClassTicketOrgEnabled.value) {
          classTicketField.tooltip = t('member.memberInfoBlock.classTicketTooltip')
        }
        fields.push(classTicketField)
      }
      return fields
    })

    const groupedPointFields = computed(() => {
      const orgFields = []
      const shopFields = []
      const shopPoints = useShopPoint.value ? memberTopInfo?.value?.shopPoints || [] : []
      const shopPointWallets = memberTopInfo?.value?.shopPointWallets || []
      const pointWalletBalance = useShopPoint.value ? props.pointWalletBalance : {}

      if (shopPoints.length) {
        for (const point of shopPoints) {
          const composedWalletKey = `${point.sourceShopId}_${point.key}`
          const walletKey =
            get(props.pointWalletBalance, composedWalletKey) ||
            shopPointWallets.some((wallet) => wallet.shopPointKey === composedWalletKey)
              ? composedWalletKey
              : point.key

          const pointWallet =
            get(props.pointWalletBalance, composedWalletKey) ||
            get(props.pointWalletBalance, point.key)

          const rawWallet =
            shopPointWallets.find((wallet) => wallet.shopPointKey === composedWalletKey) ||
            shopPointWallets.find((wallet) => wallet.shopPointKey === point.key)

          const balance = pointWallet?.balance ?? rawWallet?.balance ?? 0

          const field = {
            fieldKey: walletKey,
            label: point.name,
            value: `${balance} ${point.unitName || ''}`.trim(),
            icon: enableOrgBenefitSharing.value && point.sourceShopIsHeadquarter ? 'common-org_icon' : 'common-shop_icon',
          }
          if (enableOrgBenefitSharing.value && point.sourceShopIsHeadquarter) {
            orgFields.push(field)
          } else {
            shopFields.push(field)
          }
        }
      } else {
        for (const objKey in pointWalletBalance) {
          const point = get(pointWalletBalance, objKey)
          const balance = get(point, 'balance')
          const field = {
            fieldKey: objKey,
            label: point.pointName,
            value: `${balance ?? '-'} ${point.pointUnitName}`,
            icon: enableOrgBenefitSharing.value && point.sourceShopIsHeadquarter ? 'common-org_icon' : 'common-shop_icon',
          }
          if (enableOrgBenefitSharing.value && point.sourceShopIsHeadquarter) {
            orgFields.push(field)
          } else {
            shopFields.push(field)
          }
        }
      }

      if (props.useExternalPoint) {
        const balance = props.externalPointWalletBalance
        orgFields.push({
          fieldKey: 'external-point',
          label: t('member.memberInfoBlock.externalPoints'),
          value: balance === '-' ? balance : `${balance} ${t('member.memberInfoBlock.pointsUnit')}`,
        })
      }
      return { orgFields, shopFields }
    })

    return {
      balanceFields,
      groupedPointFields,
    }
  },
})
</script>

<style lang="postcss" scoped>
.user-statistic {
  @apply flex gap-5 flex-wrap;
}

.user-info-item {
  @apply min-w-[140px];
}

.info-label {
  @apply text-gray-80 text-[12px] leading-[27px];
}

.point-section {
  @apply flex flex-col gap-2;
}

.point-group-items {
  @apply flex flex-wrap gap-x-5 gap-y-2;
}
</style>
